import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { getCatalogueItems } from '../../services/api';
import usePageBanner from '../../hooks/usePageBanner';
import { AutoTranslate } from '../../hooks/useAutoTranslate';
import TranslatedContent from '../../components/TranslatedContent';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Package, Search, Filter, X } from 'lucide-react';

const CATEGORIES = [
  { value: 'all', label: { sv: 'Alla', en: 'All' } },
  { value: 'bibles', label: { sv: 'Biblar', en: 'Bibles' } },
  { value: 'rosaries', label: { sv: 'Rosenkransar', en: 'Rosaries' } },
  { value: 'books', label: { sv: 'Böcker', en: 'Books' } },
  { value: 'icons', label: { sv: 'Ikoner', en: 'Icons' } },
  { value: 'other', label: { sv: 'Övrigt', en: 'Other' } },
];

const CataloguePage = () => {
  const { language, isRTL, t } = useLanguage();
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('catalogue', language);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const data = await getCatalogueItems({ active_only: true });
        setItems(data);
      } catch (error) {
        console.error('Error fetching catalogue:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const name = item.name?.[language] || item.name?.sv || '';
    const description = item.description?.[language] || item.description?.sv || '';
    const matchesSearch = !searchQuery || 
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.code.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group items by category for display
  const groupedItems = selectedCategory === 'all' 
    ? CATEGORIES.filter(c => c.value !== 'all').map(cat => ({
        category: cat,
        items: filteredItems.filter(item => item.category === cat.value)
      })).filter(group => group.items.length > 0)
    : [{ category: CATEGORIES.find(c => c.value === selectedCategory), items: filteredItems }];

  return (
    <div className={`min-h-screen themed-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <Navbar />
      
      {/* Header Banner */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage || '/images/default-banner.jpg'}
          alt="Catalogue"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4">
              {bannerTitle ? (
                <TranslatedContent content={{ sv: bannerTitle }} as="span" />
              ) : (
                <AutoTranslate>Katalog</AutoTranslate>
              )}
            </h1>
            <p className="text-xl text-gray-200">
              {bannerSubtitle ? (
                <TranslatedContent content={{ sv: bannerSubtitle }} as="span" />
              ) : (
                <AutoTranslate>Biblar, rosenkransar, böcker och heliga föremål</AutoTranslate>
              )}
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 py-12">
        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'sv' ? 'Sök i katalogen...' : 'Search catalogue...'}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-500" />
            {CATEGORIES.map(cat => (
              <Button
                key={cat.value}
                variant={selectedCategory === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.value)}
                className={selectedCategory === cat.value 
                  ? 'bg-amber-600 hover:bg-amber-700' 
                  : ''}
              >
                {cat.label[language] || cat.label.sv}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              <AutoTranslate>Inga artiklar hittades</AutoTranslate>
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {groupedItems.map(({ category, items }) => (
              <section key={category.value}>
                {selectedCategory === 'all' && (
                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6 border-b pb-2">
                    {category.label[language] || category.label.sv}
                  </h2>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {items.map(item => (
                    <Card 
                      key={item.id} 
                      className="overflow-hidden hover:shadow-xl card-hover transition-shadow cursor-pointer group"
                      onClick={() => setSelectedItem(item)}
                    >
                      <div className="relative h-48 bg-gray-100">
                        {item.images && item.images.length > 0 ? (
                          <img 
                            src={item.images[0]} 
                            alt={item.name?.[language] || item.name?.sv}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <Package className="w-16 h-16 text-gray-300" />
                          </div>
                        )}
                        {!item.in_stock && (
                          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                            <AutoTranslate>Slut i lager</AutoTranslate>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-500 font-mono mb-1">{item.code}</p>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {item.name?.[language] || item.name?.sv || 'Namnlös'}
                        </h3>
                        {item.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                            {item.description?.[language] || item.description?.sv}
                          </p>
                        )}
                        {item.price && (
                          <p className="text-lg font-bold text-amber-600">
                            {item.price} kr
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Contact Info */}
        <div className="mt-16 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
            <AutoTranslate>Intresserad av att köpa?</AutoTranslate>
          </h3>
          <p className="text-gray-600 mb-4">
            <AutoTranslate>
              Kontakta oss för att beställa eller fråga om tillgänglighet.
            </AutoTranslate>
          </p>
          <Button 
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => window.location.href = '/contact'}
          >
            <AutoTranslate>Kontakta oss</AutoTranslate>
          </Button>
        </div>
      </main>

      {/* Item Detail Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image Gallery */}
            {selectedItem.images && selectedItem.images.length > 0 && (
              <div className="relative h-64 sm:h-80 bg-gray-100">
                <img 
                  src={selectedItem.images[0]} 
                  alt={selectedItem.name?.[language] || selectedItem.name?.sv}
                  className="w-full h-full object-contain"
                />
                <button
                  onClick={() => setSelectedItem(null)}
                  className="absolute top-4 right-4 bg-white/90 rounded-full p-2 hover:bg-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            
            <div className="p-6">
              <p className="text-sm text-gray-500 font-mono mb-1">{selectedItem.code}</p>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                {selectedItem.name?.[language] || selectedItem.name?.sv}
              </h2>
              
              {selectedItem.price && (
                <p className="text-2xl font-bold text-amber-600 mb-4">
                  {selectedItem.price} kr
                </p>
              )}
              
              {selectedItem.description && (
                <p className="text-gray-600 mb-6">
                  {selectedItem.description?.[language] || selectedItem.description?.sv}
                </p>
              )}
              
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-sm ${
                  selectedItem.in_stock 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {selectedItem.in_stock 
                    ? (language === 'sv' ? 'I lager' : 'In stock')
                    : (language === 'sv' ? 'Slut i lager' : 'Out of stock')
                  }
                </span>
                <span className="text-sm text-gray-500">
                  {CATEGORIES.find(c => c.value === selectedItem.category)?.label[language] || selectedItem.category}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CataloguePage;
